import React, { useEffect, useState, useRef } from "react";
import ChecklistModal from "../../components/standalonefs/ChecklistModal";
import ReferenceModal from "../../components/standalonefs/ReferenceModal";
import RptLineLedgerModal from "../../components/standalonefs/RptLineLedgerModal";
import commoncss from "../../css/common.module.css";
import libcss from "../../css/library.module.css";
import swal from "sweetalert";
import "./FSTable.css";
import { ReactComponent as PlusIcon } from "../../assets/icons/plusprimary.svg";
import AddRPTTransactionModal from "../../../WorkSpace/Components/Modal/AddRPTTransactionModal";
import DeletePopup from "../../elements/DeletePopup";

import { ReactComponent as DelIcon } from "../../assets/icons/delete_red.svg";
import { ReactComponent as EditIcon } from "../../assets/icons/editPri.svg";
import { ReactComponent as LinkIcon } from "../../assets/icons/Link.svg";
import linkedIcon from "../../assets/icons/linked.png";
import FormulaIcon from "../../assets/icons/formulaIcon.png";
import BoldIcon from "../../assets/icons/BoldIcon.png";
import { ReactComponent as InfoIcon } from "../../assets/icons/infoPrimary.svg";
import tableStyles from "../../css/workingsTable.module.css";

import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import MenuIcon from "../../assets/icons/Menu.png";
import Popover from "@mui/material/Popover";
import MoreVertIcon from "@mui/icons-material/MoreVert";

const genObjectId = () => {
  return Array.from({ length: 24 }, () =>
    Math.floor(Math.random() * 16).toString(16)
  ).join("");
};

const isValueColumn = (col) => {
  const fieldLower = String(col.field || "").toLowerCase();
  const labelLower = String(col.label || "").toLowerCase();
  return (
    col.type === "number" ||
    col.align === "right" ||
    fieldLower.includes("year") ||
    labelLower.includes("year") ||
    fieldLower.includes("amount") ||
    labelLower.includes("amount") ||
    fieldLower.includes("value") ||
    labelLower.includes("value")
  );
};

const normalizeFormulaKey = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ")
    // SOCIE labels are sometimes edited only to add/remove a trailing dash.
    .replace(/[\s\-–—]+$/g, "");

const getFormulaRowName = (row) => {
  const knownNames = [
    row?.particular,
    row?.particulars,
    row?.row_header,
    row?.rowHeader,
    row?.name,
    row?.description,
    row?.Particular,
    row?.Particulars,
  ];
  const foundName = knownNames.find(
    (value) =>
      typeof value === "string" && value.trim() !== ""
  );
  if (foundName !== undefined) return foundName;

  const metadataKeys = new Set([
    "_id", "id", "rowid", "row_id", "rowid", "linetype", "line_type",
    "cellformulas", "formula", "formulatype", "isbold", "references",
    "reference", "groupingid", "derivedfromworkings",
  ]);
  const entry = Object.entries(row || {}).find(([key, value]) =>
    !metadataKeys.has(key.toLowerCase().replace(/[\s-]/g, "")) &&
    (typeof value === "string" || typeof value === "number") &&
    String(value).trim() !== ""
  );
  return entry?.[1] ?? "";
};

export default function FSTable({
  columns: rawColumns = [],
  rows = [],
  onRowsChange = () => {},
  onCellChangeImmediate = () => {},
  onRowAdd = null,        // (position, row) => void  — called when user commits a new row
  onRowDelete = null,     // (rowIdx, updatedRows) => void
  onRowReorder = null,    // (fromIndex, toIndex) => void
  brandingClass = "ft-branding",
  emptyMessage = "No data for this category",
  containerWidth = "100%",
  redirectTo = () => {},
  checklistRef = () => {},
  tableTitle = null,
  tableId = null,
  tableType = "table",
  deletePopulateFS = () => {},
  onEditTable = null,
  onRowBoldToggle = null,
  enableHoverLink = false,
  hoverLinkColumns = [],
  cellFormulas = [],
  onCellLinkClick = () => {},
  tableDataID = null,
  allowDragAndAdd = false,
  activeCat = "",
  selectedCell = null,
  showLinkPanel = false,
  useRowActionMenu = null,
  onRowDetails = null,
}) {
  const columns = React.useMemo(() => {
    return rawColumns.filter((col) => {
      const fieldLower = String(col.field || "").toLowerCase().trim();
      const labelLower = String(col.label || "").toLowerCase().trim();
      return (
        fieldLower !== "groupingid" && 
        fieldLower !== "grouping id" && 
        labelLower !== "groupingid" && 
        labelLower !== "grouping id" &&
        fieldLower !== "formula" &&
        labelLower !== "formula"
      );
    });
  }, [rawColumns]);

  const [localRows, setLocalRows] = useState(
    Array.isArray(rows) ? [...rows] : []
  );
  const [searchFilter, setSearchFilter] = useState({});
  const [deleteConfirmData, setDeleteConfirmData] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);

  const [editing, setEditing] = useState(null);
  const inputRef = useRef(null);
  const [showChecklistModal, setShowChecklistModal] = useState(false);
  const [viewReferenceModal, setViewReferenceModal] = useState(false);
  const [modalData, setModalData] = useState([]);
  const [refrenceModalData, setReferenceModalData] = useState(null);
  const [showAddRPTTransactionModal, setShowAddRPTTransactionModal] = useState(false);
  const [rptLedgerModalData, setRptLedgerModalData] = useState(null);

  const isRowLocked = (row) => {
    if (activeCat !== "socie") return false;
    const label = (row?.particular || "").trim().toLowerCase();
    const normalized = label.replace(/\s+/g, " ").trim();
    return (
      normalized.includes("balance at the beginning") ||
      normalized.includes("restated balance") ||
      normalized.includes("total comprehensive income") ||
      normalized.includes("balance at the end") ||
      normalized === "total"
    );
  };

  const [rowMenuAnchor, setRowMenuAnchor] = useState(null);

  const handleRowMenuOpen = (e, rIdx) => {
    e.stopPropagation();
    setRowMenuAnchor({ el: e.currentTarget, rIdx });
  };

  const handleRowMenuClose = () => {
    setRowMenuAnchor(null);
  };

  const isActionMenuTable = React.useMemo(() => {
    if (typeof useRowActionMenu === "boolean") return useRowActionMenu;
    const tType = String(tableType || "").toLowerCase().trim();
    const tTitle = String(tableTitle || "").toLowerCase().trim();
    return (
      tType === "transactions" ||
      tType === "outstanding balances" ||
      tType === "transaction" ||
      tType === "outstanding balance" ||
      tTitle.includes("transaction") ||
      tTitle.includes("outstanding balance")
    );
  }, [useRowActionMenu, tableType, tableTitle]);

  useEffect(() => {
    setLocalRows(Array.isArray(rows) ? [...rows] : []);
    setEditing(null);
    setRowMenuAnchor(null);
  }, [rows]);

  useEffect(() => {
    if (editing && inputRef.current) {
      try {
        inputRef.current.focus();
      } catch (e) {}
    }
  }, [editing]);

  const startEdit = (rowIdx, col) => {
    if (!col.editable) return;
    if (col.type === "checkbox") return; 
    if ((localRows[rowIdx]?.lineType || "").toLowerCase() === "header") return;
    if (isRowLocked(localRows[rowIdx])) return;
    setEditing({ rowIdx, field: col.field, col });
  };

  const commitValue = (valueStr) => {
    if (!editing) return;
    const { rowIdx, field, col } = editing;
    const updated = [...localRows];
    let newVal = valueStr;

    if (col.type === "number") {
      newVal = valueStr === "" ? null : Number(valueStr);
      if (isNaN(newVal)) newVal = null;
    } else if (col.type === "array") {
      newVal = String(valueStr)
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean);
    }

    updated[rowIdx] = { ...updated[rowIdx], [field]: newVal };

    // When a brand-new row gets its first value typed, seal it and fire onRowAdd
    const wasNew = localRows[rowIdx]?.isNewRow === true;
    if (wasNew && newVal !== null && newVal !== "") {
      updated[rowIdx] = { ...updated[rowIdx], isNewRow: false };
      setLocalRows(updated);
      setEditing(null);
      if (typeof onRowAdd === "function") {
        try { onRowAdd(rowIdx, updated[rowIdx]); } catch (e) { console.error(e); }
      } else {
        try { onRowsChange(updated); } catch (e) { console.error(e); }
      }
      return;
    }

    setLocalRows(updated);
    setEditing(null);

    if (!updated[rowIdx]?.isNewRow) {
      try {
        onRowsChange(updated);
      } catch (e) {
        console.error("onRowsChange error", e);
      }
    }
  };

  // Emit single-object immediate change: { _id, key, value }
  const emitImmediateChange = (rowIdx, key, value) => {
    if (activeCat === "cashflow" && key === "particular") return;
    const row = localRows[rowIdx] ?? {};
    if (row.isNewRow) return;
    const id = row._id ?? rowIdx;
    try {
      onCellChangeImmediate({ _id: id, key, value, tableId });
    } catch (e) {
      console.error("onCellChangeImmediate error", e);
    }
  };

  // Handles single-value checkbox option columns (field: "value", optionValue: "Y"/"N"/"NA")
  const toggleSingleValueCheckbox = (rowIdx, col, checked) => {
    if (!col.optionValue) {
      console.warn("[FSTable] checkbox column missing optionValue", col);
      return;
    }
    const updated = [...localRows];
    const row = { ...updated[rowIdx] };

    if (checked) {
      row[col.field] = col.optionValue;
    } else {
      row[col.field] = null;
    }

    updated[rowIdx] = row;
    setLocalRows(updated);

    // immediate emit single-object diff
    emitImmediateChange(rowIdx, col.field, row[col.field]);

    if (!row.isNewRow) {
      try {
        onRowsChange(updated);
      } catch (e) {
        console.error("onRowsChange error", e);
      }
    }
  };

  const cancel = () => setEditing(null);

  const formatIndianNumber = (val) => {
    if (val == null) return "";
    const num = Math.round(Number(val));
    if (isNaN(num)) return String(val);
    if (num < 0) {
      return "(" + (-1 * num).toLocaleString("en-IN") + ")";
    }
    return num.toLocaleString("en-IN");
  };

  const displayValue = (row, field, col) => {
    if (row.level === 1 && row.lineType !== "formula") {
      const fieldLower = String(field || "").toLowerCase();
      if (fieldLower.includes("currentyear") || fieldLower.includes("previousyear")) {
        return "";
      }
    }
    const v = row[field];
    if (v == null || v === "") {
      if (row.isNewRow) {
        return isValueColumn(col) ? "0" : "Enter text";
      }
      return "";
    }
    if (Array.isArray(v)) return v.join(", ");
    if (col.type === "number" || isValueColumn(col)) return formatIndianNumber(v);
    if (col.type === "checkbox") return "";
    return String(v);
  };

  const normalizedContainerWidth =
    typeof containerWidth === "number"
      ? `${containerWidth}px`
      : containerWidth || "100%";

  const handleChecklistModal = (data) => {
    setModalData([]);
    const modalDataObj = {
      header: data.reference,
      libRef: data.libraryReference,
    };
    setModalData(modalDataObj);
    setShowChecklistModal(true);
  };

  const handleReferenceModal = (data) => {
    setReferenceModalData(data);
    setViewReferenceModal(true);
  };

  const handleDeleteFSTable = () => {
    setDeleteConfirmData(true);
  };

  const addNewRowAt = (insertIdx) => {
    const newRowId = genObjectId();
    const newRow = {
      _id: newRowId,
      isNewRow: true,
      lineType: "line item",
    };
    columns.forEach((c) => {
      newRow[c.field] = "";
    });
    // Preserve scroll position — adding a row must not jump the page
    const scrollEl = document.querySelector(".standalone-scroll-container") || window;
    const savedScrollTop = scrollEl === window ? window.scrollY : scrollEl.scrollTop;

    const updated = [...localRows];
    updated.splice(insertIdx, 0, newRow);
    setLocalRows(updated);
    // No API call here — API fires only when user types (commitValue)
    requestAnimationFrame(() => {
      if (scrollEl === window) window.scrollTo(0, savedScrollTop);
      else scrollEl.scrollTop = savedScrollTop;
    });
  };

  const addNewRow = () => {
    addNewRowAt(localRows.length);
  };

  const deleteRow = (rIdx) => {
    const updated = localRows.filter((_, i) => i !== rIdx);
    setLocalRows(updated);
    if (typeof onRowDelete === "function") {
      try { onRowDelete(rIdx, updated); } catch (e) { console.error(e); }
    } else {
      try { onRowsChange(updated); } catch (e) { console.error(e); }
    }
  };

  const toggleBold = (rIdx) => {
    const updated = [...localRows];
    updated[rIdx] = { ...updated[rIdx], isBold: !updated[rIdx].isBold };
    setLocalRows(updated);
    
    if (typeof onRowBoldToggle === "function") {
      try { onRowBoldToggle(rIdx, updated[rIdx]); } catch (e) { console.error(e); }
    } else if (typeof onRowsChange === "function") {
      try { onRowsChange(updated, tableId); } catch (e) { console.error(e); }
    }
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const fromIdx = result.source.index;
    const toIdx = result.destination.index;
    if (fromIdx === toIdx) return;

    if (activeCat === "socie") {
      let minBound = -1;
      for (let i = fromIdx - 1; i >= 0; i--) {
        if (isRowLocked(localRows[i])) {
          minBound = i;
          break;
        }
      }
      let maxBound = localRows.length;
      for (let i = fromIdx + 1; i < localRows.length; i++) {
        if (isRowLocked(localRows[i])) {
          maxBound = i;
          break;
        }
      }

      if (toIdx <= minBound || toIdx >= maxBound) {
        return; // Prevent dragging across locked section boundaries
      }
    }

    const items = Array.from(localRows);
    const [reorderedItem] = items.splice(fromIdx, 1);
    items.splice(toIdx, 0, reorderedItem);
    setLocalRows(items);

    if (typeof onRowReorder === "function") {
      try { onRowReorder(fromIdx, toIdx, items); } catch (e) { console.error(e); }
    } else {
      try { onRowsChange(items); } catch (e) { console.error(e); }
    }
  };

  const hasSectionHeaders = allowDragAndAdd && localRows.some(r => (r.lineType || "").toLowerCase() === "header");

  const renderTableBody = () => {
    if (!localRows || localRows.length === 0) {
      return (
        <tbody>
          <tr>
            <td
              colSpan={columns.length}
              style={{ textAlign: "center", padding: 20 }}
            >
              {emptyMessage}
            </td>
          </tr>
        </tbody>
      );
    }

    const renderCells = (row, rIdx, dragProvided, isDragging) => {
      const rowKey = row._id ?? rIdx;
      const isLevel1BSPL = row.level === 1 && (activeCat === "BS" || activeCat === "PL");
      const isFormula = (row.originalLineType || row.lineType || "").toLowerCase() === "formula" || (row.particular || "").toLowerCase().trim() === "total";
      const currentRowId = row.rowId || row._id || tableDataID;
      const isActiveRow = showLinkPanel && selectedCell && 
        selectedCell.row === row.particular && 
        selectedCell.rowId === currentRowId;
      const cellBackground = isDragging ? "#fff" : (isLevel1BSPL ? "#eaeeed" : (isActiveRow ? "#f8f9f9" : undefined));

      return columns.map((col, cIdx) => {
        const isEditing =
          editing &&
          editing.rowIdx === rIdx &&
          editing.field === col.field;
        const inputAlignClass =
          col.align === "right"
            ? "ft-input-right"
            : col.align === "center"
            ? "ft-input-center"
            : "ft-input-left";
        const cellText = displayValue(row, col.field, col);
        const isEmptyPlaceholder = row.isNewRow && (row[col.field] == null || row[col.field] === "");
        const allowHoverLink = enableHoverLink && 
          (hoverLinkColumns === true || hoverLinkColumns.includes(col.field));

        const handleCellClick = () => {
          if (col.type === "checkbox") return;
          startEdit(rIdx, col);
        };

        return (
          <td
            key={cIdx}
            style={{
              position: "relative",
              backgroundColor: cellBackground,
              borderTop: isDragging ? "1px solid #3AB67D" : undefined,
              borderBottom: isDragging ? "1px solid #3AB67D" : undefined,
              borderLeft: (isDragging && cIdx === 0) ? "1px solid #3AB67D" : undefined,
              borderRight: (isDragging && cIdx === columns.length - 1) ? "1px solid #3AB67D" : undefined,
              minWidth:
                col.field === "currentYear" || col.field === "previousYear"
                  ? "240px"
                  : typeof col.minWidth === "number"
                  ? `${col.minWidth}px`
                  : col.minWidth,

              width:
                col.field === "currentYear" || col.field === "previousYear"
                  ? "240px"
                  : typeof col.width === "number"
                  ? `${col.width}px`
                  : col.width,
              paddingLeft: (cIdx === 0 && allowDragAndAdd && !isRowLocked(row)) ? "28px" : undefined,
              ...(activeCat === "socie" && {
                position: cIdx === 0 ? "sticky" : "relative",
                left: cIdx === 0 ? 0 : undefined,
                zIndex: cIdx === 0 ? 100 : undefined,
                backgroundColor: cIdx === 0 ? (isActiveRow ? cellBackground : "#fff") : cellBackground,
              }),
            }}
            className={`${
              col.align === "right"
                ? "ft-align-right"
                : col.align === "center"
                ? "ft-align-center"
                : "ft-align-left"
            } ${isEditing ? "ft-editing" : ""}`}
            onClick={handleCellClick}
            onDoubleClick={handleCellClick}
          >
            {cIdx === 0 && allowDragAndAdd && dragProvided && (
              <div
                {...dragProvided.dragHandleProps}
                className="ft-row-drag-handle"
                style={{
                  position: "absolute",
                  left: "8px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  display: isRowLocked(row) ? "none" : "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "14px",
                  height: "20px",
                }}
                title={isRowLocked(row) ? "" : "Drag to reorder"}
              >
                {!isRowLocked(row) && (
                  <img src={MenuIcon} alt="drag" style={{ width: 9, height: 13 }} />
                )}
              </div>
            )}

            {col.type === "popup" && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent:
                    col.align === "right"
                      ? "flex-end"
                      : col.align === "center"
                      ? "center"
                      : "flex-start",
                }}
              >
                <span
                  className={commoncss.clickable}
                  onClick={() => {
                    handleChecklistModal(row);
                  }}
                >
                  {row[col.field]}
                </span>
              </div>
            )}
            {col.type === "checklistref" && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent:
                    col.align === "right"
                      ? "flex-end"
                      : col.align === "center"
                      ? "center"
                      : "flex-start",
                }}
              >
                {Array.isArray(row[col.field])
                  ? row[col.field].map((item, index) => (
                      <span key={index}>
                        <span
                          onClick={() => {
                            handleReferenceModal(item);
                          }}
                          style={{
                            cursor: "pointer",
                            marginRight: "8px",
                            textDecoration: "underline",
                          }}
                        >
                          {item}
                        </span>
                        {index < row[col.field].length - 1 && ", "}
                      </span>
                    ))
                  : row[col.field] ?? ""}
              </div>
            )}
            {col.type === "redirect" && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent:
                    col.align === "right"
                      ? "flex-end"
                      : col.align === "center"
                      ? "center"
                      : "flex-start",
                  width: "100%",
                }}
              >
                {(row.level === 1 && (activeCat === "BS" || activeCat === "PL") && col.field === "notes") ? (
                  ""
                ) : row[col.field] ? (
                  <span
                    style={{
                      color: "#205a46",
                      textDecoration: "underline",
                      cursor: "pointer",
                      fontWeight: 600,
                    }}
                    onClick={() => {
                      redirectTo(row[col.field], row);
                    }}
                  >
                    {row[col.field]}
                  </span>
                ) : (
                  <span style={{ color: "#ccc" }}>–</span>
                )}
              </div>
            )}
            {col.type === "checkbox" ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent:
                    col.align === "right"
                      ? "flex-end"
                      : col.align === "center"
                      ? "center"
                      : "flex-start",
                }}
              >
                <input
                  name={`chk_${rowKey}_${col.field}_${col.optionValue}`}
                  className="ft-checkbox"
                  type="checkbox"
                  checked={row[col.field] === col.optionValue}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    toggleSingleValueCheckbox(rIdx, col, checked);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  aria-label={`${col.label} checkbox row ${rIdx}`}
                />
              </div>
            ) : (
              <>
                {col.type !== "redirect" &&
                  col.type !== "popup" &&
                  col.type !== "checklistref" && (
                    <div
  className={`${tableStyles.cell} ft-cell-wrapper ${(col.align === "right" && allowHoverLink) ? "ft-has-hover-link" : ""}`}
  style={{
    visibility: isEditing ? "hidden" : "visible",
    width: "100%",
    position: "relative",
    textAlign:
      col.align === "right"
        ? "right"
        : col.align === "center"
        ? "center"
        : "left",
    display: "flex",
    alignItems: "center",
    justifyContent: col.align === "right" ? "flex-end" : col.align === "center" ? "center" : "flex-start",
  }}
>
                      {/* Value and Link Icon Logic */}
                      {(() => {
                        let matchedFormula = null;
                        if (allowHoverLink && cellFormulas && cellFormulas.length > 0) {
                          const rowName = normalizeFormulaKey(getFormulaRowName(row));
                          const rowIdVal = normalizeFormulaKey(row.rowId || row.rowID || row._id || row.id);
                          const colName = normalizeFormulaKey(col.field || col.label);
                          const cleanKey = (v) => String(v || "").toLowerCase().replace(/[^a-z0-9]/g, "");
                          const cleanRow = cleanKey(rowName);

                          matchedFormula = cellFormulas.find(f => {
                            const fRow = normalizeFormulaKey(f.row || f.rowID || f.rowId);
                            const fRowId = normalizeFormulaKey(f.rowId || f.rowID);
                            const fCol = normalizeFormulaKey(f.col || f.column || f.columnName);
                            const cleanFRow = cleanKey(fRow);

                            return fCol === colName && (
                              fRow === rowName ||
                              (cleanFRow && cleanRow && cleanFRow === cleanRow) ||
                              (activeCat === "socie" && rowIdVal &&
                                (fRow === rowIdVal || fRowId === rowIdVal))
                            );
                          });
                        }

                        const isLinked = matchedFormula;
                        const isRightAligned = col.align === "right";
                        const useAbsolute = isRightAligned;

                        return (
                          <>
                            <span
                              style={{
                                color: isEmptyPlaceholder ? "#aaa" : "inherit",
                                fontStyle: isEmptyPlaceholder ? "italic" : "normal",
                                fontWeight: (isLevel1BSPL || row.isBold || row.isbold || matchedFormula?.isBold || matchedFormula?.isbold) ? "bold" : "inherit",
                              }}
                            >
                              {cellText}
                            </span>
                            
                            {allowHoverLink && (
                              <span
                                className={`
                                  ${tableStyles.linkWrapper}
                                  ${isLinked ? tableStyles.linked : ""}
                                  ft-cell-link-icon
                                  ${isLinked ? "ft-cell-link-icon-linked" : ""}
                                `}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onCellLinkClick(row, col, tableDataID, isLinked);
                                }}
                                style={{
                                  background: isLinked ? "#205a46" : "#3AB67D",
                                  borderRadius: "12px",
                                  marginLeft: isLinked ? "6px" : (isRightAligned ? "0px" : "8px"),
                                  cursor: "pointer",
                                  width: "25px",
                                  height: "24px",
                                  flexShrink: 0,
                                  position: useAbsolute ? "absolute" : "static",
                                  right: useAbsolute ? "2px" : undefined,
                                  top: useAbsolute ? "50%" : undefined,
                                  transform: useAbsolute ? "translateY(-50%)" : undefined,
                                }}
                              >
                                {isLinked ? (
                                  (isLinked.type === "formula" || isLinked.terms) ? (
                                    <img src={FormulaIcon} style={{ width: 16, height: 16 }} alt="formula" />
                                  ) : (
                                    <img src={linkedIcon} className={tableStyles.linkedIcon} style={{ width: 20, height: 10 }} alt="link" />
                                  )
                                ) : (
                                  <LinkIcon style={{ width: 14, height: 14, color: "#fff" }} />
                                )}
                              </span>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  )}

                {isEditing &&
                  col.type !== "redirect" &&
                  col.type !== "popup" &&
                  col.type !== "checklistref" && (
                    <input
                      ref={inputRef}
                      className={`ft-edit-input ${inputAlignClass} ${(cIdx === columns.length - 1 && allowDragAndAdd && !isRowLocked(row)) ? "ft-edit-input-last-cell" : ""}`}
                      style={{ fontWeight: (row.isBold || row.isbold) ? "bold" : "inherit" }}
                      type={
                        col.type === "number" ? "number" : "text"
                      }
                      defaultValue={
                        col.type === "array" &&
                        Array.isArray(row[col.field])
                          ? row[col.field].join(", ")
                          : row[col.field] ?? ""
                      }
                      placeholder={row.isNewRow ? (isValueColumn(col) ? "0" : "Enter text") : (col.placeholder ?? "")}
                      onBlur={(e) => commitValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter")
                          commitValue(e.target.value);
                        if (e.key === "Escape") cancel();
                      }}
                      onKeyUp={(e) => {
                        const raw = e.target.value;
                        // compute outgoing according to type
                        let outgoing = raw;
                        if (col.type === "number") {
                          const n =
                            raw === "" ? null : Number(raw);
                          outgoing = isNaN(n) ? null : n;
                        } else if (col.type === "array") {
                          outgoing = String(raw)
                            .split(",")
                            .map((v) => v.trim())
                            .filter(Boolean);
                        }
                        // update localRows so UI is responsive while typing
                        const updated = [...localRows];
                        updated[rIdx] = {
                          ...updated[rIdx],
                          [col.field]: outgoing,
                        };
                        setLocalRows(updated);
                        // emit single-object immediate diff
                        if (!(activeCat === "cashflow" && col.field === "particular")) {
                          emitImmediateChange(
                            rIdx,
                            col.field,
                            outgoing
                          );
                        }
                      }}
                    />
                  )}
              </>
            )}

            {/* Action icons - last cell only */}
            {cIdx === columns.length - 1 && (allowDragAndAdd || typeof onRowDelete === "function") && (
              isActionMenuTable ? (
                <div
                  className={`ft-row-menu-icon ${rowMenuAnchor?.rIdx === rIdx ? "active" : ""}`}
                  onClick={(e) => handleRowMenuOpen(e, rIdx)}
                  title="More options"
                >
                  <MoreVertIcon style={{ fontSize: 18, color: "#1A4338" }} />
                </div>
              ) : (
                <>
                  {allowDragAndAdd && (
                    <div
                      className={`ft-row-bold-icon ${row.isBold ? "active" : ""}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleBold(rIdx);
                      }}
                      title={row.isBold ? "Remove Bold" : "Make Bold"}
                    >
                      <img src={BoldIcon} alt="Bold" style={{ width: 14, height: 14 }} />
                    </div>
                  )}
                  {!isRowLocked(row) && typeof onRowDelete === "function" && (
                    <div
                      className="ft-row-delete-icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        setRowToDelete(rIdx);
                      }}
                      title="Delete row"
                    >
                      <DelIcon style={{ width: 16, height: 16 }} />
                    </div>
                  )}
                </>
              )
            )}
          </td>
        );
      });
    };


    const addLineItemButton = (insertIdx) => (
      <tr key={`add-line-${insertIdx}`}>
        <td colSpan={columns.length} style={{ border: "none", padding: "4px 0" }}>
          <button
            onClick={() => addNewRowAt(insertIdx)}
            style={{
              background: "none",
              border: "none",
              color: "#205a46",
              cursor: "pointer",
              fontWeight: "500",
              padding: "8px 12px 8px 0px",
              fontSize: "14px",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <PlusIcon style={{ width: 14, height: 14, marginTop: "-2px"  }} /> Add Line Item
          </button>
        </td>
      </tr>
    );

    const renderRows = () => {
      const elements = [];

      localRows.forEach((row, rIdx) => {
        const rowKey = row._id ?? rIdx;
        const isHeader = (row.lineType || "").toLowerCase() === "header";
        const isFormula = (row.originalLineType || row.lineType || "").toLowerCase() === "formula" || (row.particular || "").toLowerCase().trim() === "total";

        // Before each header (except the first), insert an "Add Line Item" for the previous section
        if (isHeader && rIdx > 0 && hasSectionHeaders) {
          elements.push(addLineItemButton(rIdx));
        }

        if (isHeader && !isFormula) {
          elements.push(
            <tr key={rowKey}>
              <td
                colSpan={columns.length}
                style={{
                  background: "#eaeeed",
                  color: "#33584e",
                  fontWeight: 600,
                  textAlign: "left",
                  ...(activeCat === "socie" && {
                    position: "sticky",
                    left: 0,
                    zIndex: 100,
                  }),
                }}
              >
                {row.particular}
              </td>
            </tr>
          );
          return;
        }

        const normalize = (str) => String(str || "").replace(/[^a-z0-9]/gi, "").toLowerCase();
        const rowId = normalize(row.particular);
        
        const currentRowId = row.rowId || row._id || tableDataID;
        const isActiveRow = showLinkPanel && selectedCell && 
          selectedCell.row === row.particular && 
          selectedCell.rowId === currentRowId;

        const isLevel1BSPL = row.level === 1 && (activeCat === "BS" || activeCat === "PL");
        const rowBackground = isLevel1BSPL ? "#eaeeed" : (isActiveRow ? "#f8f9f9" : undefined);
        const explicitBold = row.isBold !== undefined ? row.isBold : row.isbold;
        const isRowBold = explicitBold !== undefined ? (explicitBold === true || explicitBold === "true") : (isFormula || isLevel1BSPL);
        const rowFontWeight = isRowBold ? "bold" : "normal";

        if (allowDragAndAdd) {
          const locked = isRowLocked(row);
          elements.push(
            <Draggable key={rowKey} draggableId={String(rowKey)} index={rIdx} isDragDisabled={locked}>
              {(dragProvided, snapshot) => {
                const isDragging = snapshot.isDragging;
                const draggingStyle = isDragging ? {
                  backgroundColor: "#fff",
                  boxShadow: "0px 10px 20px rgba(0,0,0,0.15)",
                  zIndex: 9999,
                  display: "table"
                } : {};
                return (
                <tr
                  ref={dragProvided.innerRef}
                  {...dragProvided.draggableProps}
                  id={rowId}
                  style={{
                    ...dragProvided.draggableProps.style,
                    background: isDragging ? "#fff" : rowBackground,
                    fontWeight: rowFontWeight,
                    ...draggingStyle
                  }}
                >
                  {columns.length === 0 && (
                    <td style={{ display: "none" }} {...dragProvided.dragHandleProps} />
                  )}
                  {renderCells(row, rIdx, dragProvided, isDragging)}
                </tr>
              )}}
            </Draggable>
          );
        } else {
          elements.push(
            <tr key={rowKey} id={rowId} style={{ background: rowBackground, fontWeight: rowFontWeight }}>
              {renderCells(row, rIdx, null, false)}
            </tr>
          );
        }
      });

      // Add button at the end for the last section
      if (hasSectionHeaders) {
        elements.push(addLineItemButton(localRows.length));
      }

      return elements;
    };

    if (allowDragAndAdd) {
      return (
        <Droppable droppableId={String(tableId || "fs-table-droppable")} type="ROW">
          {(provided) => (
            <tbody ref={provided.innerRef} {...provided.droppableProps}>
              {renderRows()}
              {provided.placeholder}
            </tbody>
          )}
        </Droppable>
      );
    }

    return <tbody>{renderRows()}</tbody>;
  };

  const getHeaderLabel = (label) => {
    if (!label || typeof label !== "string") return label;

    const lowerLabel = label.toLowerCase();
    
    // For Balance Sheet: "As at ..." on a single line
    if (lowerLabel.startsWith("as at ")) {
      return <span style={{ whiteSpace: "nowrap" }}>{label}</span>;
    }

    // For P&L: "For the period ended ..." on two lines
    if (lowerLabel.startsWith("for the period ended ")) {
      // Split the string after "ended"
      const prefix = label.substring(0, 20); // "For the period ended" is 20 chars
      const datePart = label.substring(20).trim();
      return (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", width: "100%" }}>
          <span style={{ whiteSpace: "nowrap" }}>{prefix}</span>
          <span style={{ whiteSpace: "nowrap" }}>{datePart}</span>
        </div>
      );
    }

    return label;
  };

  const renderTable = () => {
    const tableElement = (
      <table
        className={`ft-table ${brandingClass} ft-scrTable ${tableType === "related party" ? "rpt-table" : ""} ${isActionMenuTable ? "ft-action-menu-table" : ""}`}
        style={{ width: "100%", tableLayout: activeCat === "disclosure" ? "auto" : "fixed" }}
      >
        <thead style={activeCat === "socie" ? { position: "sticky", top: 0, zIndex: 150, backgroundColor: "#fff" } : undefined}>
          {(tableTitle || onEditTable) && (
            <tr className={`ft-table-title-row ${["BS notes", "PL notes", "disclosure"].includes(activeCat) ? "ft-table-title-white" : ""} ${["general notes", "disclosure"].includes(activeCat) ? "ft-table-title-no-padding-right" : ""}`} style={activeCat === "socie" ? { position: "sticky", top: 0, zIndex: 151 } : undefined}>
              <th colSpan={columns.length} style={{ position: "relative", ...(activeCat === "socie" ? { position: "sticky", top: 0, zIndex: 151, background: "#ffffff" } : {}) }}>
                <div className="ft-table-title" style={{ position: activeCat === "socie" ? "sticky" : "static", left: activeCat === "socie" ? "10px" : undefined, width: "100%", display: "flex", alignItems: "center" }}>
                  <span>{tableTitle || "Table Settings"}</span>
                  <div style={{ display: "flex", gap: "10px", alignItems: "center", position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)" }}>
                    {onEditTable && (
                      <EditIcon 
                        style={{ cursor: "pointer", width: 16, height: 16 }} 
                        onClick={onEditTable} 
                      />
                    )}
                    {(tableType === "link register" || (tableId && deletePopulateFS)) && (
                      <DelIcon 
                        style={{ cursor: "pointer", width: 16, height: 16 }} 
                        onClick={handleDeleteFSTable} 
                      />
                    )}
                  </div>
                </div>
              </th>
            </tr>
          )}
          <tr>
            {columns.map((c, i) => (
              <th
                key={i}
                style={{
                  minWidth:
                    c.field === "currentYear" || c.field === "previousYear"
                      ? "240px"
                      : typeof c.minWidth === "number"
                      ? `${c.minWidth}px`
                      : c.minWidth,

                  maxWidth:
                    c.field === "currentYear" || c.field === "previousYear"
                      ? "240px"
                      : typeof c.maxWidth === "number"
                      ? `${c.maxWidth}px`
                      : c.maxWidth,

                  width:
                    c.field === "currentYear" || c.field === "previousYear"
                      ? "240px"
                      : typeof c.width === "number"
                      ? `${c.width}px`
                      : c.width,

                  whiteSpace: c.headerWhiteSpace || "normal",
                  wordBreak: "break-word",
                  lineHeight: "1.2",
                  boxShadow: `inset 0 -1px 0 #ccd5d3`,
                  paddingLeft: (i === 0 && allowDragAndAdd) ? "28px" : undefined,
                  textTransform: ["general notes", "disclosure"].includes(activeCat) ? "none" : undefined,
                  ...(activeCat === "socie" && {
                    position: "sticky",
                    top: (tableTitle || onEditTable) ? 32 : 0,
                    zIndex: i === 0 ? 155 : 105,
                    backgroundColor: "#eef6ef",
                    ...(i === 0 && { left: 0 })
                  }),
                  paddingRight: (c.align === "right" && enableHoverLink && (hoverLinkColumns === true || hoverLinkColumns.includes(c.field))) ? "32px" : undefined,
                  textAlign: c.align === "right" ? "right" : c.align === "center" ? "center" : "left",
                }}
                className={
                  c.align === "right"
                    ? "ft-align-right"
                    : c.align === "center"
                    ? "ft-align-center"
                    : "ft-align-left"
                }
              >
               {getHeaderLabel(c.label)}
              </th>
            ))}
          </tr>
        </thead>

        {renderTableBody()}

        {allowDragAndAdd && !hasSectionHeaders && (
          <tfoot>
            <tr>
              <td colSpan={columns.length} style={{ border: "none", padding: "8px 0" }}>
                <button
                  onClick={addNewRow}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#205a46",
                    cursor: "pointer",
                    fontWeight: "500",
                    padding: "8px 12px 8px 0px",
                    fontSize: "14px",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  <PlusIcon style={{ width: 14, height: 14, marginTop: "-2px" }} /> Add Line Item
                </button>
              </td>
            </tr>
          </tfoot>
        )}
      </table>
    );

    if (allowDragAndAdd) {
      return (
        <DragDropContext onDragEnd={onDragEnd}>
          {tableElement}
        </DragDropContext>
      );
    }

    return tableElement;
  };

  return (
    <div
      style={{
        width: normalizedContainerWidth,
        marginTop: tableType === "related party" ? "0px" : "20px",
      }}
      id={tableId}
    >
      <div 
        className="ft-table-scroll"
        style={{
          border: "1px solid #ccd5d3",
          borderRadius: tableType === "notes" ? "10px" : "12px",
          borderCollapse: "collapse",
          width: "100%",
          overflowX: "auto",
          overflowY: activeCat === "socie" ? "auto" : undefined,
          ...(activeCat === "socie" && {
            maxHeight: "calc(100vh - 140px)"
          }),
        }}
      >
        {renderTable()}
      </div>
      {showChecklistModal && modalData && (
        <ChecklistModal
          setShowChecklistModal={setShowChecklistModal}
          modalData={modalData}
        />
      )}
      {viewReferenceModal && refrenceModalData && (
        <ReferenceModal
          setViewReferenceModal={setViewReferenceModal}
          modalData={refrenceModalData}
        />
      )}
      {showAddRPTTransactionModal && (
        <AddRPTTransactionModal
          setShowAddRPTTransactionModal={setShowAddRPTTransactionModal}
        />
      )}
      {rptLedgerModalData && (
        <RptLineLedgerModal
          isOpen={Boolean(rptLedgerModalData)}
          onClose={() => setRptLedgerModalData(null)}
          row={rptLedgerModalData.row}
          tableTitle={rptLedgerModalData.tableTitle}
          heading={rptLedgerModalData.heading}
          onSuccess={(savedPayload) => {
            const updated = [...localRows];
            const rIdx = localRows.findIndex(
              (r) =>
                r === rptLedgerModalData.row ||
                (r._id && r._id === rptLedgerModalData.row._id)
            );
            if (rIdx !== -1) {
              updated[rIdx] = { ...updated[rIdx], ...savedPayload };
              setLocalRows(updated);
            }
          }}
        />
      )}
      {/* Row action menu popover */}
      <Popover
        open={Boolean(rowMenuAnchor)}
        anchorEl={rowMenuAnchor?.el}
        onClose={handleRowMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          style: {
            boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.12)",
            borderRadius: "8px",
            border: "1px solid #EAEEED",
            backgroundColor: "#FFFFFF",
            padding: "4px 0",
            minWidth: "140px",
          },
        }}
      >
        {(() => {
          if (!rowMenuAnchor) return null;
          const menuRowIdx = rowMenuAnchor.rIdx;
          const menuRow = localRows[menuRowIdx];
          if (!menuRow) return null;

          const explicitBold = menuRow.isBold !== undefined ? menuRow.isBold : menuRow.isbold;
          const isRowBold = explicitBold !== undefined ? (explicitBold === true || explicitBold === "true") : false;
          const canDelete = !isRowLocked(menuRow) && typeof onRowDelete === "function";

          const menuRowLineType = String(menuRow.lineType || "").toLowerCase().trim();
          const isHeadingRow = menuRowLineType === "heading" || menuRowLineType === "header";
          const showDetails = !isHeadingRow;

          return (
            <div className="ft-row-menu-dropdown" style={{ display: "flex", flexDirection: "column" }}>
              {allowDragAndAdd && (
                <div
                  className="ft-row-menu-item"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRowMenuClose();
                    toggleBold(menuRowIdx);
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    padding: "8px 14px",
                    cursor: "pointer",
                    fontSize: "13px",
                    color: "#33584e",
                    fontWeight: isRowBold ? "600" : "500",
                    transition: "background-color 0.15s ease",
                  }}
                >
                  <img src={BoldIcon} alt="Bold" style={{ width: 14, height: 14 }} />
                  <span>{isRowBold ? "Remove Bold" : "Bold"}</span>
                </div>
              )}
              {showDetails && allowDragAndAdd && (
                <div style={{ height: "1px", backgroundColor: "#EAEEED", margin: "2px 0" }} />
              )}
              {/* Details option with InfoIcon (only shown when lineType is not heading) */}
              {showDetails && (
                <div
                  className="ft-row-menu-item"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRowMenuClose();

                    let detectedHeading = "";
                    let detectedParty = "";

                    if (menuRow?.heading) {
                      detectedHeading = menuRow.heading;
                    }
                    if (menuRow?.relatedPartyName || menuRow?.partyName) {
                      detectedParty = menuRow.relatedPartyName || menuRow.partyName;
                    }

                    // Scan backward to find section heading above this row
                    let headingRow = null;
                    for (let i = menuRowIdx - 1; i >= 0; i--) {
                      const r = localRows[i];
                      if (!r) continue;
                      const rLineType = String(r.lineType || "").toLowerCase().trim();
                      const isExplicitHeader = rLineType === "heading" || rLineType === "header";

                      const hasAmounts = Object.keys(r).some((k) => {
                        if (["particular", "_id", "id", "lineType", "isBold", "isbold", "key"].includes(k)) return false;
                        const val = r[k];
                        return val !== null && val !== undefined && String(val).trim() !== "" && String(val).trim() !== "-";
                      });

                      if (isExplicitHeader || !hasAmounts || (r.isBold && !hasAmounts)) {
                        headingRow = r;
                        break;
                      }
                    }

                    if (headingRow && (headingRow.particular || headingRow.heading)) {
                      detectedHeading = headingRow.particular || headingRow.heading;
                      if (!detectedParty) {
                        detectedParty = menuRow?.particular || "";
                      }
                    } else {
                      if (!detectedHeading) {
                        detectedHeading = menuRow?.heading || "";
                      }
                      if (!detectedParty) {
                        detectedParty = menuRow?.particular || "";
                      }
                    }

                    // Normalize tableTitle
                    const lowerTitle = String(tableTitle || tableType || "").toLowerCase();
                    let normTableTitle = "Transactions";
                    if (lowerTitle.includes("outstanding")) {
                      normTableTitle = "Outstanding balances";
                    } else if (lowerTitle.includes("transaction")) {
                      normTableTitle = "Transactions";
                    } else if (tableTitle) {
                      normTableTitle = tableTitle;
                    }

                    setRptLedgerModalData({
                      row: {
                        ...menuRow,
                        relatedPartyName: detectedParty,
                      },
                      heading: detectedHeading,
                      tableTitle: normTableTitle,
                    });
                    if (typeof onRowDetails === "function") {
                      onRowDetails(menuRow, menuRowIdx);
                    }
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    padding: "8px 14px",
                    cursor: "pointer",
                    fontSize: "13px",
                    color: "#33584e",
                    fontWeight: "500",
                    transition: "background-color 0.15s ease",
                  }}
                >
                  <InfoIcon style={{ width: 15, height: 15 }} />
                  <span>RPT Config</span>
                </div>
              )}
              {canDelete && (showDetails || allowDragAndAdd) && (
                <div style={{ height: "1px", backgroundColor: "#EAEEED", margin: "2px 0" }} />
              )}
              {canDelete && (
                <div
                  className="ft-row-menu-item ft-row-menu-item-delete"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRowMenuClose();
                    setRowToDelete(menuRowIdx);
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    padding: "8px 14px",
                    cursor: "pointer",
                    fontSize: "13px",
                    color: "#d32f2f",
                    fontWeight: "500",
                    transition: "background-color 0.15s ease",
                  }}
                >
                  <DelIcon style={{ width: 16, height: 16 }} />
                  <span>Delete</span>
                </div>
              )}
            </div>
          );
        })()}
      </Popover>

      <DeletePopup
        isOpen={deleteConfirmData}
        title="Confirm Deletion"
        message="Once deleted, you can recover this from the bin"
        onConfirm={async () => {
          setDeleteConfirmData(false);
          await deletePopulateFS(tableId);
        }}
        onCancel={() => setDeleteConfirmData(false)}
      />
      <DeletePopup
        isOpen={rowToDelete !== null}
        title="Are you sure?"
        message={
          ["socie", "disclosure"].includes(activeCat)
            ? "Once deleted, you will not be able to recover this!"
            : "Once deleted, you can recover this from the bin"
        }
        onConfirm={() => {
          if (rowToDelete !== null) {
            deleteRow(rowToDelete);
            setRowToDelete(null);
          }
        }}
        onCancel={() => setRowToDelete(null)}
      />

    </div>
  );
}
